import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
/**
 * `/hashtag/:name` — every discussion containing a post that used this hashtag.
 *
 * The list is a stock DiscussionList driven by the `hashtag:` search filter, so
 * visibility scoping, pagination and the sort dropdown are core's rather than
 * ours. A hashtag used only in discussions the actor can't see renders the
 * same empty state as one nobody has used.
 *
 * Wrapped in PageStructure with the standard IndexSidebar so it reads as part
 * of the forum rather than a bolted-on page.
 */
export default class HashtagPage extends Page {
  /** Raw name from the route — may be in any casing. */

  /** The hashtag record, for the header counts. `null` once we know there is none. */
  hashtag = null;
  loading = true;
  oninit(vnode) {
    super.oninit(vnode);
    this.name = decodeURIComponent(m.route.param('name') || '');
    this.list = new DiscussionListState({
      filter: {
        hashtag: this.name
      }
    });
    this.list.refresh();
    app.setTitle(`#${this.name}`);

    /**
     * The header record is a nicety, not a gate — the discussion list is
     * already loading in parallel off the name alone. A 404 here just means
     * nobody is using the hashtag right now, which is a normal state for
     * freeform hashtags and renders as the empty message rather than an error.
     */
    app.store.find('hashtags', encodeURIComponent(this.name)).then(hashtag => {
      this.hashtag = hashtag;
      app.setTitle(`#${hashtag.name()}`);
    }).catch(() => {
      this.hashtag = null;
    }).then(() => {
      this.loading = false;
      m.redraw();
    });
  }
  view() {
    return m(PageStructure, {
      className: "HashtagPage Page--vertical",
      hero: this.hero.bind(this),
      sidebar: this.sidebar.bind(this)
    }, this.content());
  }
  hero() {
    // Prefer the stored display casing once we have it, so #gameday and
    // #GameDay both land on a page headed the way it was first written.
    const display = this.hashtag ? this.hashtag.name() : this.name;
    return m("div", {
      className: "Hero HashtagHero"
    }, m("div", {
      className: "container"
    }, m("h1", {
      className: "Hero-title HashtagHero-title"
    }, m("span", {
      className: "HashtagHero-hash"
    }, "#"), display), m("div", {
      className: "Hero-subtitle HashtagHero-subtitle"
    }, this.hashtag ? app.translator.trans('ernestdefoe-hashtags.forum.page.meta', {
      posts: this.hashtag.postCount(),
      discussions: this.hashtag.discussionCount()
    }) : null), m(Link, {
      className: "HashtagHero-browse",
      href: app.route('hashtags')
    }, app.translator.trans('ernestdefoe-hashtags.forum.page.browse_all'))));
  }
  sidebar() {
    return m(IndexSidebar, null);
  }
  content() {
    const display = this.hashtag ? this.hashtag.name() : this.name;
    if (this.list.isLoading()) {
      return m(LoadingIndicator, null);
    }
    if (this.list.isEmpty()) {
      return m("p", {
        className: "HashtagPage-empty"
      }, app.translator.trans('ernestdefoe-hashtags.forum.page.empty', {
        name: display
      }));
    }
    return m(DiscussionList, {
      state: this.list
    });
  }
}
flarum.reg.add('ernestdefoe-hashtags', 'forum/components/HashtagPage', HashtagPage);